// ============================================================================
// FEEDBACK HOOK
// Public feedback data operations (submit, vote, comment, fetch)
// Direct Supabase — NOT Zustand state
// ============================================================================

import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from '@/hooks/use-toast';
import type {
  FeedbackItem,
  FeedbackComment,
  FeedbackSubmission,
  FeedbackFilters,
} from '@/types/feedback';

export function useFeedback() {
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [userVotes, setUserVotes] = useState<Set<string>>(new Set());
  const [comments, setComments] = useState<FeedbackComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);

  const fetchPublicItems = useCallback(async (filters?: FeedbackFilters) => {
    setLoading(true);
    try {
      let query = supabase
        .from('feedback_items')
        .select('*')
        .eq('is_public', true);

      // Status filter
      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      } else {
        // Exclude rejected from public view
        query = query.neq('status', 'rejected');
      }

      // Category filter
      if (filters?.category && filters.category !== 'all') {
        query = query.eq('category', filters.category);
      }

      // Sort
      if (filters?.sort === 'newest') {
        query = query.order('created_at', { ascending: false });
      } else {
        query = query.order('vote_count', { ascending: false });
      }

      const { data, error } = await query;
      if (error) throw error;
      setItems(data || []);
    } catch (err) {
      console.error('[Feedback] Failed to fetch items:', err);
      toast({ title: 'Failed to load feedback', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUserVotes = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data, error } = await supabase
        .from('feedback_votes')
        .select('feedback_id')
        .eq('user_id', session.user.id);

      if (error) throw error;
      setUserVotes(new Set((data || []).map(v => v.feedback_id)));
    } catch (err) {
      console.error('[Feedback] Failed to fetch votes:', err);
    }
  }, []);

  const submitFeedback = useCallback(async (
    submission: FeedbackSubmission,
    authorName: string
  ) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({ title: 'Please sign in to submit feedback', variant: 'destructive' });
        return false;
      }

      const { error } = await supabase
        .from('feedback_items')
        .insert({
          user_id: session.user.id,
          author_display_name: authorName || 'Anonymous',
          title: submission.title,
          description: submission.description,
          category: submission.category,
          show_author: submission.show_author,
        });

      if (error) throw error;

      toast({ title: 'Feedback submitted', description: 'Thank you! Your feedback is pending review.' });
      return true;
    } catch (err) {
      console.error('[Feedback] Submit failed:', err);
      toast({ title: 'Failed to submit feedback', variant: 'destructive' });
      return false;
    }
  }, []);

  const toggleVote = useCallback(async (feedbackId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({ title: 'Please sign in to vote', variant: 'destructive' });
        return;
      }

      const hasVoted = userVotes.has(feedbackId);

      if (hasVoted) {
        // Optimistic: remove vote
        setUserVotes(prev => {
          const next = new Set(prev);
          next.delete(feedbackId);
          return next;
        });
        setItems(prev => prev.map(item =>
          item.id === feedbackId ? { ...item, vote_count: item.vote_count - 1 } : item
        ));

        const { error } = await supabase
          .from('feedback_votes')
          .delete()
          .eq('feedback_id', feedbackId)
          .eq('user_id', session.user.id);

        if (error) {
          // Revert on error
          setUserVotes(prev => new Set(prev).add(feedbackId));
          setItems(prev => prev.map(item =>
            item.id === feedbackId ? { ...item, vote_count: item.vote_count + 1 } : item
          ));
          throw error;
        }
      } else {
        // Optimistic: add vote
        setUserVotes(prev => new Set(prev).add(feedbackId));
        setItems(prev => prev.map(item =>
          item.id === feedbackId ? { ...item, vote_count: item.vote_count + 1 } : item
        ));

        const { error } = await supabase
          .from('feedback_votes')
          .insert({ feedback_id: feedbackId, user_id: session.user.id });

        if (error) {
          // Revert on error
          setUserVotes(prev => {
            const next = new Set(prev);
            next.delete(feedbackId);
            return next;
          });
          setItems(prev => prev.map(item =>
            item.id === feedbackId ? { ...item, vote_count: item.vote_count - 1 } : item
          ));
          throw error;
        }
      }
    } catch (err) {
      console.error('[Feedback] Vote toggle failed:', err);
      toast({ title: 'Vote failed', variant: 'destructive' });
    }
  }, [userVotes]);

  const fetchComments = useCallback(async (feedbackId: string) => {
    setCommentsLoading(true);
    try {
      const { data, error } = await supabase
        .from('feedback_comments')
        .select('*')
        .eq('feedback_id', feedbackId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setComments(data || []);
    } catch (err) {
      console.error('[Feedback] Failed to fetch comments:', err);
    } finally {
      setCommentsLoading(false);
    }
  }, []);

  const addComment = useCallback(async (
    feedbackId: string,
    content: string,
    authorName: string,
    isAdminReply = false
  ) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({ title: 'Please sign in to comment', variant: 'destructive' });
        return false;
      }

      const newComment: Partial<FeedbackComment> = {
        feedback_id: feedbackId,
        user_id: session.user.id,
        author_display_name: authorName || 'Anonymous',
        content,
        is_admin_reply: isAdminReply,
      };

      // Optimistic append
      const optimisticComment: FeedbackComment = {
        id: crypto.randomUUID(),
        feedback_id: feedbackId,
        user_id: session.user.id,
        author_display_name: authorName || 'Anonymous',
        content,
        is_admin_reply: isAdminReply,
        created_at: new Date().toISOString(),
      };
      setComments(prev => [...prev, optimisticComment]);

      // Update comment count optimistically on items
      setItems(prev => prev.map(item =>
        item.id === feedbackId ? { ...item, comment_count: item.comment_count + 1 } : item
      ));

      const { error } = await supabase
        .from('feedback_comments')
        .insert(newComment);

      if (error) {
        // Revert
        setComments(prev => prev.filter(c => c.id !== optimisticComment.id));
        setItems(prev => prev.map(item =>
          item.id === feedbackId ? { ...item, comment_count: item.comment_count - 1 } : item
        ));
        throw error;
      }

      // Refetch to get server-assigned id
      await fetchComments(feedbackId);
      return true;
    } catch (err) {
      console.error('[Feedback] Comment failed:', err);
      toast({ title: 'Failed to add comment', variant: 'destructive' });
      return false;
    }
  }, [fetchComments]);

  const searchFeedback = useCallback(async (query: string): Promise<FeedbackItem[]> => {
    if (!query || query.trim().length < 3) return [];

    try {
      // Search for items with matching query in title or description
      // Exclude rejected items
      // Include non-public (pending) items to prevent duplicates from other users
      const { data, error } = await supabase
        .from('feedback_items')
        .select('*')
        .neq('status', 'rejected')
        .or(`title.ilike.%${query}%,description.ilike.%${query}%`)
        .order('vote_count', { ascending: false })
        .limit(5);

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('[Feedback] Search failed:', err);
      return [];
    }
  }, []);

  // Temporary function to seed DB with initial requests
  const seedFeedback = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({ title: 'Must be logged in to seed', variant: 'destructive' });
        return;
      }

      const features = [
        {
          title: "Strava Integration",
          description: "Automatically sync completed activities from Strava to Adaptive Endurance. Include map visualization and segment data.",
          category: "feature_request",
          show_author: true,
          status: "planned",
          vote_count: 42
        },
        {
          title: "Garmin Connect Sync",
          description: "Push structured workouts directly to Garmin watches and pull completed activity data.",
          category: "feature_request",
          show_author: true,
          status: "in_progress",
          vote_count: 85
        },
        {
          title: "TrainingPeaks Export",
          description: "Allow exporting the generated ATP and weekly schedule to TrainingPeaks calendar.",
          category: "feature_request",
          show_author: false,
          status: "pending_review",
          vote_count: 12
        },
        {
          title: "Apple Health 2-way Sync",
          description: "Read sleep and HRV data from Apple Health to adjust daily readiness. Write workout data back to Health.",
          category: "feature_request",
          show_author: true,
          status: "under_review",
          vote_count: 28
        },
        {
          title: "Oura Ring Sleep Data",
          description: "Direct integration with Oura Cloud API to pull sleep scores and readiness for plan adaptation.",
          category: "feature_request",
          show_author: true,
          status: "planned",
          vote_count: 56
        },
        {
          title: "Whoop Recovery Sync",
          description: "Import Whoop recovery scores to auto-adjust daily training load limits.",
          category: "feature_request",
          show_author: true,
          status: "under_review",
          vote_count: 31
        },
        {
          title: "Custom Workout Builder",
          description: "Drag-and-drop interface to create custom interval or steady-state workouts and save them to library.",
          category: "feature_request",
          show_author: true,
          status: "pending_review",
          vote_count: 19
        }
      ];

      let successCount = 0;
      for (const feature of features) {
        // Check if exists first to avoid true duplicates in seeding
        const { data: existing } = await supabase
          .from('feedback_items')
          .select('id')
          .eq('title', feature.title)
          .single();

        if (!existing) {
          const { error } = await supabase.from('feedback_items').insert({
            user_id: session.user.id,
            author_display_name: "Rob Kranendonk",
            title: feature.title,
            description: feature.description,
            category: feature.category,
            status: feature.status,
            show_author: feature.show_author,
            vote_count: feature.vote_count,
            is_public: true // Force public for these seeded items
          });
          if (!error) successCount++;
        }
      }

      if (successCount > 0) {
        toast({ title: `Seeded ${successCount} features`, description: "Database populated successfully." });
        fetchPublicItems(); // Refresh list
      }
    } catch (err) {
      console.error('Seeding failed', err);
    }
  }, [fetchPublicItems]);

  const submitSupportTicket = useCallback(async (
    subject: string,
    message: string,
    email: string
  ) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        toast({
          title: 'Sign in required',
          description: 'Please sign in again before contacting support.',
          variant: 'destructive',
        });
        return false;
      }

      const normalizedSubject = subject.trim();
      const normalizedMessage = message.trim();
      const resolvedEmail = email.trim() || session.user.email?.trim() || '';

      if (!resolvedEmail) {
        toast({
          title: 'Missing account email',
          description: 'Please sign in again so we can send support replies.',
          variant: 'destructive',
        });
        return false;
      }

      const { error } = await supabase
        .from('support_tickets')
        .insert({
          user_id: session.user.id,
          email: resolvedEmail,
          subject: normalizedSubject,
          message: normalizedMessage,
          status: 'open'
        });

      if (error) throw error;

      toast({
        title: 'Message Sent',
        description: 'Thank you! We have received your support request and will get back to you soon.'
      });
      return true;
    } catch (err) {
      console.error('[Support] Submission failed:', err);
      toast({ title: 'Failed to send message', description: 'Please try again later or email us directly at support@adaptiveendurance.app', variant: 'destructive' });
      return false;
    }
  }, []);

  return {
    items,
    userVotes,
    comments,
    loading,
    commentsLoading,
    fetchPublicItems,
    fetchUserVotes,
    submitFeedback,
    submitSupportTicket,
    toggleVote,
    fetchComments,
    addComment,
    searchFeedback,
    seedFeedback
  };
}
